/**
 * The pill over the composer while a send's attachments go up (chat-plan.md
 * D398): its title, its numbers and its bar, read out of the send's
 * `UploadState`. The desktop's `state/upload.rs`, rule for rule.
 *
 * The numbers are the bytes on the wire, not the files the sender picked (the
 * owner's call, 2026-10-05, over «7,0 из 14,1 МБ» for a picture that went up
 * as 180 KB): a photograph goes up as a few hundred kilobytes of WebP (D233)
 * however large its original — and a pasted one may be a full-size PNG. How
 * many is unknown until an attachment's encode is over, so each one counts
 * from the moment it is weighed: the numbers are blank while the first is
 * encoding, and the total grows as the next ones are weighed. The bar is not
 * theirs: it is each attachment's own progress, weighted by the picked sizes,
 * which are known at the press — so it never goes back.
 */
import type { FileKind } from "../gen/dto";
import type { UploadItem, UploadKind, UploadState } from "../store/types";
import type { Dict } from "./i18n";

/** What a kind on its way out counts as, by the kind it travels as — `null`
 * for a voice note, which never raises the pill: nobody picked it, it is tens
 * of kilobytes, and a pill flashing over every note would be noise. */
export function uploadKindOf(kind: FileKind): UploadKind | null {
  switch (kind) {
    case "photo":
      return "photo";
    case "video":
    case "video_message":
      return "video";
    case "voice":
      return null;
    default:
      return "file";
  }
}

/** The pill for a send of these attachments, as `[send kind, picked size]` —
 * or `null` when there is nothing for it to show: no attachments, or a voice
 * note, which never shares a send with anything else. */
export function newUpload(
  channelId: number,
  nonce: string,
  attachments: ReadonlyArray<readonly [FileKind, number]>,
): UploadState | null {
  if (attachments.length === 0) return null;
  const items: UploadState["items"] = [];
  for (const [kind, weight] of attachments) {
    const counted = uploadKindOf(kind);
    if (counted === null) return null;
    items.push({ kind: counted, weight, wire: null, sent: 0, progress: 0 });
  }
  return { channelId, nonce, items };
}

/** Writes how many of one attachment's bytes on the wire are up, of how many —
 * the second number weighs it. */
export function setUploadSent(item: UploadItem, sent: number, wire: number): void {
  item.wire = wire;
  item.sent = Math.min(wire, Math.max(0, Math.floor(sent)));
}

/** The bytes on the wire of the attachments weighed so far. */
export function uploadTotal(upload: UploadState): number {
  return upload.items.reduce((sum, item) => sum + (item.wire ?? 0), 0);
}

/** How many of the total are up. */
export function uploadDone(upload: UploadState): number {
  const done = upload.items.reduce((sum, item) => sum + item.sent, 0);
  return Math.min(done, uploadTotal(upload));
}

/** «120,4 из 180,5 КБ» — or nothing while no attachment has been weighed yet:
 * the first one is still encoding. */
export function uploadAmount(upload: UploadState, t: Dict): string {
  if (upload.items.every((item) => item.wire === null)) return "";
  return t.uploadAmount(uploadDone(upload), uploadTotal(upload));
}

/** 0..=1 for the bar: each item's progress times its picked size, or — when
 * no size was known — the mean of the items' own progress, so the bar still
 * moves. */
export function uploadFraction(upload: UploadState): number {
  const weight = upload.items.reduce((sum, item) => sum + item.weight, 0);
  if (weight > 0) {
    const done = upload.items.reduce((sum, item) => sum + item.weight * clamp01(item.progress), 0);
    return clamp01(done / weight);
  }
  const sum = upload.items.reduce((s, item) => s + clamp01(item.progress), 0);
  return clamp01(sum / Math.max(1, upload.items.length));
}

/** The one kind every attachment travels as, or `null` once two meet. */
export function uploadKind(upload: UploadState): UploadKind | null {
  const first = upload.items[0]?.kind;
  if (first === undefined) return null;
  return upload.items.every((item) => item.kind === first) ? first : null;
}

/** «Отправка 2 фото» — or «Отправка медиа», with no number, when the
 * attachments are of more than one kind (the owner's rule). */
export function uploadTitle(upload: UploadState, t: Dict): string {
  const count = upload.items.length;
  switch (uploadKind(upload)) {
    case "photo":
      return t.uploadPhotos(count);
    case "video":
      return t.uploadVideos(count);
    case "file":
      return t.uploadFiles(count);
    case null:
      return t.uploadMedia();
  }
}

/** The square's glyph: the kind, or a paperclip once two kinds meet. */
export function uploadGlyph(upload: UploadState): string {
  switch (uploadKind(upload)) {
    case "photo":
      return "file-image";
    case "video":
      return "video";
    case "file":
      return "file-text";
    case null:
      return "paperclip";
  }
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}
